import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Grid,
  CircularProgress,
  Chip
} from '@mui/material';
import { collection, query, getDocs, getDoc, doc } from 'firebase/firestore';
import { db, functions } from '../../../../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { SUBSCRIPTION_TYPES, DURATION_UNITS } from './constants';
import { FaUserShield, FaChalkboardTeacher } from 'react-icons/fa';
import './dialogs.css';

// # Helper function to calculate end date based on duration and unit
const calculateEndDate = (startDate, duration, unit) => {
  const date = new Date(startDate);
  switch (unit) {
    case 'minutes':
      date.setMinutes(date.getMinutes() + duration);
      break;
    case 'days':
      date.setDate(date.getDate() + duration);
      break;
    case 'weeks':
      date.setDate(date.getDate() + (duration * 7));
      break;
    case 'months':
      date.setMonth(date.getMonth() + duration);
      break;
    default:
      throw new Error('Invalid duration unit');
  }
  return date;
};

const GiveDialog = ({ 
  open, 
  onClose, 
  onSubmit, 
  formData, 
  setFormData, 
  selectedUser,
  selectedSubscriptionType
}) => {
  const subscriptionInfo = SUBSCRIPTION_TYPES[selectedSubscriptionType.toUpperCase()];
  const currentSubscription = selectedUser?.subscriptions[selectedSubscriptionType];

  // # Initialize state with minutes as default
  const [duration, setDuration] = useState('1');
  const [durationUnit, setDurationUnit] = useState('months');
  const [mentors, setMentors] = useState([]);
  const [selectedMentor, setSelectedMentor] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // # Fetch available mentors when dialog opens for mentorship subscriptions
  useEffect(() => {
    const fetchMentors = async () => {
      if (!subscriptionInfo.requiresMentor) return;
      
      try {
        setIsLoading(true);
        // # Get the administration document
        const emailsDoc = await getDoc(doc(db, 'administration', 'emails'));
        const emailsData = emailsDoc.exists() ? emailsDoc.data() : {};
        
        // # Get admin and tutor emails
        const adminEmails = emailsData.admins || [];
        const tutorEmails = emailsData.tutors || [];
        
        // # Get all users
        const usersQuery = query(collection(db, 'users'));
        const usersSnapshot = await getDocs(usersQuery);
        
        // # Create combined mentors list with role information
        const mentorsList = [];
        
        // # Process admin users
        usersSnapshot.docs
          .filter(doc => adminEmails.includes(doc.data().email))
          .forEach(doc => {
            mentorsList.push({
              uid: doc.id,
              email: doc.data().email,
              name: `${doc.data().firstName} ${doc.data().lastName}`.trim(),
              firstName: doc.data().firstName || '',
              lastName: doc.data().lastName || '',
              role: 'admin'
            });
          });
        
        // # Process tutor users
        usersSnapshot.docs
          .filter(doc => tutorEmails.includes(doc.data().email))
          .forEach(doc => {
            mentorsList.push({
              uid: doc.id,
              email: doc.data().email,
              name: `${doc.data().firstName} ${doc.data().lastName}`.trim(),
              firstName: doc.data().firstName || '',
              lastName: doc.data().lastName || '',
              role: 'tutor'
            });
          });
        
        setMentors(mentorsList);
      } catch (error) {
        console.error('Error fetching mentors:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (open) {
      fetchMentors();
      // # Reset form values when dialog opens
      setDuration('1');
      setDurationUnit('months');
      setSelectedMentor('');
    }
  }, [open, subscriptionInfo.requiresMentor]);

  // # Handle mentor selection
  const handleMentorChange = (event) => {
    const selectedMentorData = mentors.find(mentor => mentor.email === event.target.value);
    if (selectedMentorData) {
      setSelectedMentor(event.target.value);
    }
  };

  // # Handle form submission for Cloud Function
  const handleSubmit = () => {
    const startDate = new Date();
    
    // # Find selected mentor's complete data
    const selectedMentorData = mentors.find(mentor => mentor.email === selectedMentor);
    
    // # Log the values being sent
    console.log('GiveDialog submitting with:', {
      duration,
      durationUnit,
      startDate: startDate.toISOString()
    });

    // # Calculate end date for logging
    const endDate = calculateEndDate(startDate, duration, durationUnit);

    // # Create submission data for Cloud Function
    const submissionData = {
      status: 'active',
      startDate: startDate.toISOString(),
      duration: parseInt(duration),
      durationUnit: durationUnit,
      ...(subscriptionInfo.requiresMentor && selectedMentorData && {
        mentor: {
          ...selectedMentorData,
          displayRole: selectedMentorData.role === 'admin' ? 'Admin (Mentor)' : 'Tutor'
        }
      })
    };

    console.log('Final submission data:', submissionData);
    
    // # Log the subscription change to the subscription_logs collection
    const logSubscription = httpsCallable(functions, 'logSubscription');
    logSubscription({
      uid: selectedUser.uid,
      subscriptionType: selectedSubscriptionType,
      action: 'give',
      // # Metadata for give action
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      // # Additional metadata
      duration: parseInt(duration),
      durationUnit: durationUnit,
      ...(subscriptionInfo.requiresMentor && selectedMentorData && {
        newMentor: selectedMentorData.uid
      })
    })
    .then((result) => {
      console.log('Subscription logged successfully:', result.data);
    })
    .catch((error) => {
      console.error('Error logging subscription:', error);
    });

    // # The mentor document will be updated in the Cloud Function when the subscription is processed
    // # This happens in the updateSubscription Cloud Function which adds the mentee to the mentor's document
    // # in the appropriate map (mentees_step1, mentees_step2, oet_mentee, or visa_mentee)
    onSubmit(submissionData);
  };

  // # Handle duration unit change with logging
  const handleDurationUnitChange = (event) => {
    const newUnit = event.target.value;
    console.log('Duration unit changed to:', newUnit);
    setDurationUnit(newUnit);
  };

  // # Get current mentor display
  const getCurrentMentorDisplay = () => {
    const selectedMentorData = mentors.find(mentor => mentor.email === selectedMentor);
    if (!selectedMentorData) return 'None selected';
    return `${selectedMentorData.name} (${selectedMentorData.email}) - ${selectedMentorData.role === 'admin' ? 'Admin' : 'Tutor'}`;
  };

  // # Render role chip
  const RoleChip = ({ role }) => (
    <Chip
      icon={role === 'admin' ? <FaUserShield size={12} /> : <FaChalkboardTeacher size={12} />}
      label={role === 'admin' ? 'Admin' : 'Tutor'}
      size="small"
      color={role === 'admin' ? 'primary' : 'secondary'}
      sx={{ ml: 1 }}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className="dialog-title">
        Give {subscriptionInfo.name}
        {selectedUser && (
          <Typography variant="subtitle2" className="dialog-subtitle">
            to {selectedUser.userName} ({selectedUser.email})
          </Typography>
        )}
      </DialogTitle>
      <DialogContent className="dialog-content">
        {currentSubscription?.status === 'active' && (
          <div className="warning-text">
            <Typography variant="subtitle2" color="error">
              Warning: User has an active subscription
            </Typography>
            <Typography>
              Type: {subscriptionInfo.name}
              <br />
              Ends: {new Date(currentSubscription.endDate).toLocaleDateString()}
              {subscriptionInfo.requiresMentor && (
                <>
                  <br />
                  Current Mentor: {getCurrentMentorDisplay()}
                </>
              )}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Creating a new subscription will replace the current one.
            </Typography>
          </div>
        )}

        <div className="form-section">
          <Typography className="form-section-title">
            {currentSubscription?.status === 'active' 
              ? 'New Replacement Subscription' 
              : 'New Subscription Details'}
          </Typography>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Duration"
                value={duration}
                onChange={(e) => {
                  const value = Math.max(1, parseInt(e.target.value) || 1);
                  setDuration(value.toString());
                }}
                inputProps={{ min: 1 }}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth required>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={durationUnit}
                  onChange={handleDurationUnitChange}
                  label="Unit"
                >
                  {DURATION_UNITS.map((unit) => (
                    <MenuItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            Subscription duration will be exactly: {duration} {durationUnit}
          </Typography>

          {subscriptionInfo.requiresMentor && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Select Mentor
              </Typography>
              {isLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <CircularProgress size={24} />
                  <Typography>Loading mentors...</Typography>
                </div>
              ) : (
                <FormControl fullWidth required>
                  <InputLabel>Select Mentor</InputLabel>
                  <Select
                    value={selectedMentor}
                    onChange={handleMentorChange}
                    label="Select Mentor"
                  >
                    {mentors.map((mentor) => (
                      <MenuItem 
                        key={mentor.uid} 
                        value={mentor.email}
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div>
                          {mentor.name} ({mentor.email})
                          <RoleChip role={mentor.role} />
                        </div>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <Typography className="info-text" sx={{ mt: 1 }}>
                Note: Only administrators and tutors can be assigned as mentors.
              </Typography>
            </>
          )}
        </div>
      </DialogContent>
      <DialogActions className="dialog-actions">
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="contained" 
          color={currentSubscription?.status === 'active' ? 'warning' : 'primary'}
          disabled={
            !duration || 
            !durationUnit || 
            (subscriptionInfo.requiresMentor && !selectedMentor) ||
            isLoading
          }
        >
          {currentSubscription?.status === 'active' 
            ? 'Replace & Activate New Subscription' 
            : 'Activate New Subscription'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GiveDialog;
